import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {OX_KEYS} from '@ox/config';
import {AdobeLaunchService, selectFeatureFlag} from '@ox/data';
import {
  AllPaymentMethodsAPIM,
  BaseReq,
  CreditCardAPIM,
  CtaType,
  FORM_ERROR,
  pageTypes,
  Profile,
  UIFeatureFlag
} from '@ox/typings';
import {
  AppConfigService,
  AutoDestroy,
  buildTrackingString,
  LinkPipe,
  SessionStorageService,
  TrackingService,
  WINDOW
} from '@ox/utils';
import {PaymentMethodService, ShopDataService} from '@webu/data';
import {cloneDeep, isEqual, merge} from 'lodash';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {catchError, filter, Subject, takeUntil, tap} from 'rxjs';
import {EditPaymentTrackingData} from '@webu/data';
import {DOCUMENT} from '@angular/common';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ox/translate';

@Component({
  selector: 'webu-edit-credit-card',
  templateUrl: './edit-credit-card.component.html',
  styleUrls: ['./edit-credit-card.component.scss']
})
export class EditCreditCardComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() public paymentMethod: AllPaymentMethodsAPIM; // local copy
  pmtMeth: AllPaymentMethodsAPIM; // parent copy
  loading = true;
  loadError = false;
  language = this.appConfigService.bml.lang;
  months =
    this.appConfigService.appSettings.months[this.language]?.map(
      ({name, value}: {name: string; value: string}) => ({
        name,
        value: (parseInt(value) + 1).toString().padStart(2, '0')
      })
    ) || [];
  years = this.expYears;
  expDate: boolean;
  errorSaving = false;
  success = false;
  disableSaveButton = true;
  hasError = false;
  savingPaymentInfo = false;
  @ViewChild('modal') el: ElementRef;
  @Output() buttonClick: EventEmitter<string> = new EventEmitter();
  // form controls
  accountHolderName = new FormControl('', {
    validators: [
      Validators.required,
      Validators.pattern(this.attr.address.address.name?.pattern?.source),
      Validators.maxLength(170)
    ]
  });
  accountHolderAddress = new FormControl('', {
    validators: [
      Validators.required,
      Validators.pattern(this.attr.address.address.pattern?.source),
      Validators.maxLength(this.attr.address.address?.maxLength)
    ]
  });
  accountHolderCity = new FormControl('', {
    validators: [
      Validators.required,
      Validators.pattern(this.attr.address.city.pattern?.source),
      Validators.maxLength(this.attr.address.city?.maxLength)
    ]
  });
  stateProvince = new FormControl('');
  postalCode = new FormControl('', {
    validators: [
      Validators.required,
      Validators.pattern(this.attr.address.postalCode.pattern?.source),
      Validators.maxLength(this.attr.address.postalCode?.maxLength),
      Validators.minLength(this.attr.address.postalCode?.minLength)
    ]
  });
  expirationMonth = new FormControl('');
  expirationYear = new FormControl('');
  updatePmthForm = new FormGroup({
    accountHolderName: this.accountHolderName,
    accountHolderAddress: this.accountHolderAddress,
    accountHolderCity: this.accountHolderCity,
    stateProvince: this.stateProvince,
    postalCode: this.postalCode,
    expirationMonth: this.expirationMonth,
    expirationYear: this.expirationYear
  });
  disableClickTracking? = '';
  trackingString = (cta: CtaType, args: string[]) => buildTrackingString(pageTypes.payment, cta, args);  

  constructor(
    private appConfigService: AppConfigService,
    private paymentMethodService: PaymentMethodService,
    private linkPipe: LinkPipe,
    private shopDataService: ShopDataService,
    private session: SessionStorageService,
    public bsModalRef: BsModalRef,
    public ACDL:AdobeLaunchService,
    private store:Store,
    private trackingService: TrackingService,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(WINDOW) private window: Window
  ) {
    this.store.select(selectFeatureFlag({id: UIFeatureFlag.PAYMENT_HISTORY_REDESIGN, group: 'UI'})).pipe(
      filter((val)=>!!val),
      takeUntil(this.destroy$),
      catchError((err) => {
        console.error('Error getting feature flag', err);
        return [];
      }),
      tap((res) => {
        this.disableClickTracking = this.window.gm.appContext === 'public-charging' || res === 'false' ? 'disable-click-tracking' : undefined;
      })).subscribe();
  }

  get attr() {
    return this.appConfigService.fieldAttributes;
  }

  get expYears() {
    const currentYear =
      Number(this.pmtMeth?.creditCard?.expirationYear) ||
      new Date().getFullYear();
    return Array.from({length: 25}, (_, i) => (currentYear + i).toString());
  }

  ngOnInit() {
    this.setPaymentMethod();
    this.watchForChanges();
    this.ACDL.handleFormEventFromOX({form:new EditPaymentTrackingData().start()});
  }

  setFormValues({
    displayName,
    addressLine1,
    addressCity,
    addressStateProv,
    addressZipCode,
    expirationMonth,
    expirationYear
  }: CreditCardAPIM) {
    this.accountHolderName.setValue(displayName);
    this.accountHolderAddress.setValue(addressLine1);
    this.accountHolderCity.setValue(addressCity);
    this.stateProvince.setValue(addressStateProv);
    this.postalCode.setValue(addressZipCode);
    this.expirationMonth.setValue(expirationMonth);
    this.expirationYear.setValue(expirationYear);
  }

  watchForChanges() {
    this.updatePmthForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: ({
        accountHolderName: displayName, // alias the form props to match the original object
        accountHolderAddress: addressLine1,
        accountHolderCity: addressCity,
        stateProvince: addressStateProv,
        postalCode: addressZipCode,
        expirationMonth: expirationMonth,
        expirationYear: expirationYear
      }) => {
        if (this.paymentMethod && this.paymentMethod.creditCard) {
          this.paymentMethod.creditCard = merge(this.paymentMethod.creditCard, {
            displayName,
            addressLine1,
            addressCity,
            addressStateProv,
            addressZipCode,
            expirationMonth,
            expirationYear
          });
        }
      }
    });
  }

  updateCreditCard() {
    this.savingPaymentInfo = true;
    const cardInfo = this.paymentMethod as unknown as BaseReq;
    const pmid = this.paymentMethod.id || '';
    const account = this.session.getFromJson<Profile>(
      OX_KEYS.OWNER_PROFILE_KEY
    );

    this.shopDataService
      .updatePaymentMethod(account.onStarAcctId, cardInfo, pmid)
      .subscribe({
        next: (updateData) => {
          if (updateData['data']) {
            this.trackingService.fireClickTracking(
              this.trackingService.setupPropsForTracking(
                pageTypes.checkout,
                'button',
                ['save'],
                'save'));
            this.onUpdateSuccess(account.onStarAcctId);
          }
          
        },
        error: () => {
          this.onUpdateError();
        }
        
      });
  }

  public async isFormChanged(event?: Event) {
    this.expirationMonth.setAsyncValidators(
      this.expirationLessthanCurrentDate()
    );
    this.expirationYear.setAsyncValidators(
      this.expirationLessthanCurrentDate()
    );

    // Wait for async validation to complete
    await Promise.all([
      this.expirationMonth.updateValueAndValidity({emitEvent: false}),
      this.expirationYear.updateValueAndValidity({emitEvent: false})
    ]);
    
    const {creditCard} = this.paymentMethod;
    const isValidDate =
      !(this.expirationYear.hasError('invalidDate') ||
      this.expirationMonth.hasError('invalidDate'));

    let isFieldEmpty = false;
    if (event) {
      const input = event.target as HTMLInputElement;
      isFieldEmpty = !input.value || input.value.trim() === '';
    }

    this.disableSaveButton =
      !isValidDate ||
      this.isObjectChanged(
        this.pmtMeth?.creditCard as AllPaymentMethodsAPIM,
        creditCard as AllPaymentMethodsAPIM
      ) ||
      isFieldEmpty;
  }

  getPaymentMethodImage = (paymentMethod: AllPaymentMethodsAPIM) =>
    this.linkPipe.transform(
      this.paymentMethodService.getPaymentMethodImageUrl(paymentMethod)
    );

  getIsPaymentMethodExpiring = (paymentMethod: AllPaymentMethodsAPIM) =>
    this.paymentMethodService.isPaymentMethodExpiring(paymentMethod);

  displayErrorRedText(pmtMeth: AllPaymentMethodsAPIM): boolean {
    return (
      pmtMeth.creditCard?.expiryStatus === 'EXPIRED' ||
      this.getIsPaymentMethodExpiring(pmtMeth)
    );
  }

  private expirationLessthanCurrentDate(): AsyncValidatorFn {
    return (): Promise<ValidationErrors | null> => {
      const month = Number(this.expirationMonth.value);
      const year = Number(this.expirationYear.value);
      const isValidDate =
        this.isValidYear(year) && this.isValidMonth(year, month);
      if (isValidDate && this.paymentMethod && this.paymentMethod.creditCard) {
        this.paymentMethod.creditCard.expiryStatus = 'VALID';
        this.setValidDate();
        return Promise.resolve(null);
      } else {
        this.setInvalidDate();
        return Promise.resolve({invalidDate: true});
      }
    };
  }

  private setPaymentMethod() {
    this.pmtMeth = cloneDeep(this.paymentMethod);
    this.setFormValues(this.paymentMethod.creditCard as CreditCardAPIM);
    this.loading = false;
  }

  private onUpdateSuccess(accountId: string) {
    this.success = true;
    // trigger refresh of selected payment method
    this.paymentMethodService.refreshTrigger = {updating: true, accountId};
    this.ACDL.handleFormEventFromOX({form:new EditPaymentTrackingData().complete()});
    this.bsModalRef.hide();
  }

  private onUpdateError() {
    this.errorSaving = true;
    this.el.nativeElement.scrollIntoView(true);
    this.doc.dispatchEvent(new CustomEvent(FORM_ERROR,{detail:new EditPaymentTrackingData().error()}));
  }

  private isObjectChanged(
    newPmtMeth: AllPaymentMethodsAPIM | undefined,
    oldPmtMeth: AllPaymentMethodsAPIM | undefined
  ) {
    if (newPmtMeth === undefined || oldPmtMeth === undefined) {
      return false;
    }
    return isEqual(newPmtMeth, oldPmtMeth);
  }

  private setInvalidDate() {
    this.disableSaveButton = true;
    this.expDate = true;
  }

  private setValidDate() {
    this.disableSaveButton = false;
    this.expDate = false;
  }

  private isValidYear(expiryYear: number) {
    const currentYear = new Date().getFullYear();
    return expiryYear >= currentYear;
  }

  private isValidMonth(expirationYear: number, expirationMonth: number) {
    const expiryDate = new Date(
      Number(expirationYear),
      Number(expirationMonth) - 1
    );
    const currentDate = new Date();
    // Set hours, minutes, seconds, and milliseconds to 0 for a fair comparison
    currentDate.setHours(0, 0, 0, 0);
    // Compare the expiry date to the current date
    return expiryDate >= currentDate;
  }
  public closeEditCreditCard(){
    this.setFormValues(this.pmtMeth.creditCard as CreditCardAPIM); //Reset to initial values
    this.trackingService.fireClickTracking(
      this.trackingService.setupPropsForTracking(
        pageTypes.checkout,
        'button',
        ['cancel'],
        'cancel'));
    this.ACDL.handleFormEventFromOX({form: new EditPaymentTrackingData().cancel()});
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.closeEditCreditCard();
  }
}
